import { getCriticalPaths } from "@utilities/getCriticalPaths";
import { getGlobalSettings } from "@utilities/getGlobalSettings";
import { getProgrammeImportData } from "@utilities/getProgrammeImportData";
import { parse, stringify } from "flatted";
import PageComponent from "../components/Page/Page";
import {
  DEFAULT_CONTENTFUL_INCLUDE,
  fetchEntries,
  getPageProps,
} from "../utils/cms/ContentfulService";

const HOMEPAGE_SLUG = "home";

const contentTypes = [
  "content",
  "eeLandingPage",
  "content2019Page",
  "storyPage",
  "newsDetail",
  "eventsDetail",
  "bsrContentPage",
  "publicationDetail",
  "dpoPage",
  "studentProfileDetail",
  "contributorProfileDetail",
  "facultyProfileDetail",
  "embagTemplate",
  "clusterPage",
  "campaignLandingPage2019",
  "interactSso",
  "pdfAsset",
  "programmeDetails",
  "ResearchDetailsPage",
];

export default function Page({ safeJson }) {
  const { globalContent, pageContent, settings, programmeData, preview } =
    parse(safeJson);

  return (
    <PageComponent
      props={{
        globalContent,
        pageContent,
        settings,
        programmeData,
        preview,
        canonicalUrl:
          pageContent.slug === HOMEPAGE_SLUG ? "/" : `/${pageContent.slug}`,
      }}
    />
  );
}

export async function getStaticPaths() {
  // Call an external API endpoint to get posts
  const fetchPromises = contentTypes.map((contentType) =>
    fetchEntries({ content_type: contentType }).then((entries) =>
      entries.map((entry) => entry.fields.slug)
    )
  );

  const allPagesArrays = await Promise.all(fetchPromises);

  const allSlugs = allPagesArrays.flat();

  // Critical pages
  const criticalPaths: Array<string> = await getCriticalPaths();

  const paths = allSlugs
    .filter((slug) => {
      return slug !== "404" && slug !== "home" && criticalPaths.includes(slug);
    })
    .map((slug) => ({ params: { slug: slug.split("/") } }));

  return { paths, fallback: "blocking" };
}

// During the build, we want to generate a site status file
// This is used by the monitoring system to check if the site is up
// We only want to do this during the build, not on every request
// So we use a function that is only called during the build
// This function is not exported, so it is not called on every request
// It is only called when the file is imported during the build
// We use a dynamic import to avoid issues with Next.js static analysis
const generateSiteStatus = (maintenanceMode) => {
  if (process.env.SITE_STATUS_GENERATED) {
    return;
  }
  process.env.SITE_STATUS_GENERATED = "true";
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { buildSiteStatus } = require("../utils/cms/siteStatus");
  try {
    buildSiteStatus({
      maintenanceMode,
      version: process.env.PACKAGE_VERSION,
      buildDate: process.env.BUILD_DATE,
    });
  } catch (er) {
    console.error("Error generating site status:", er);
  }
};

export async function getStaticProps({ params, preview }) {
  const { slug } = params;
  try {
    const { settings, ...otherGlobals } = await getGlobalSettings();
    generateSiteStatus(!!settings?.siteSettings?.turnOnSiteMaintenanceMode);
    const { globalContent, pageContent } = await getPageProps({
      slug: slug?.join("/") || HOMEPAGE_SLUG,
      contentType: contentTypes,
      globals: [],
      include: DEFAULT_CONTENTFUL_INCLUDE,
      allTags: otherGlobals.tags,
      preview,
    });

    // If this is a programme page
    const programmeData = await getProgrammeImportData(
      pageContent?.programmeTypeCode
    );

    // https://github.com/vercel/next.js/discussions/10992#discussioncomment-103826
    const safeJson = stringify({
      globalContent: { ...globalContent, ...otherGlobals },
      pageContent,
      settings,
      programmeData,
      ...(!!preview && { preview: true }),
    });

    return {
      // Revalidate 4 times a day
      revalidate: 60 * 60 * 6,
      props: {
        safeJson,
      },
    };
  } catch (_e) {
    return { notFound: true };
  }
}
